
import { supabase } from '@/integrations/supabase/client';
import { TokenEncryption } from '@/utils/encryption/tokenEncryption';

export interface CredentialsValidationResult {
  isValid: boolean;
  needsRefresh: boolean;
  error?: string;
}

export class SecureCredentialsService {
  /**
   * Store encrypted Microsoft credentials
   */
  static async storeCredentials(
    userId: string,
    credentials: {
      access_token: string;
      refresh_token: string;
      expires_at: string;
      scope: string;
      token_type: string;
    }
  ): Promise<void> {
    try {
      console.log('Encrypting and storing credentials for user:', userId);
      
      // Encrypt the tokens before storing
      const encryptedCredentials = await TokenEncryption.encryptCredentials({
        access_token: credentials.access_token,
        refresh_token: credentials.refresh_token
      });

      const { error } = await supabase
        .from('microsoft365_credentials')
        .upsert({
          user_id: userId,
          access_token: encryptedCredentials.encrypted_access_token,
          refresh_token: encryptedCredentials.encrypted_refresh_token,
          expires_at: credentials.expires_at,
          scope: credentials.scope,
          token_type: credentials.token_type,
          calendar_enabled: true,
          outlook_enabled: true
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error storing encrypted credentials:', error);
        throw new Error('Failed to store encrypted credentials');
      }

      console.log('Successfully stored encrypted credentials');
    } catch (error) {
      console.error('Error in storeCredentials:', error);
      throw error;
    }
  }

  /**
   * Retrieve and decrypt Microsoft credentials
   */
  static async getCredentials(userId: string): Promise<{
    access_token: string;
    refresh_token: string;
    expires_at: string;
    scope: string;
    token_type: string;
  } | null> {
    try {
      console.log('Retrieving credentials for user:', userId);
      
      const { data, error } = await supabase
        .from('microsoft365_credentials')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error || !data) {
        console.log('No credentials found for user:', userId);
        return null;
      }

      console.log('Found encrypted credentials, decrypting...');
      
      // Decrypt the tokens
      const decryptedCredentials = await TokenEncryption.decryptCredentials({
        encrypted_access_token: data.access_token,
        encrypted_refresh_token: data.refresh_token
      });

      return {
        access_token: decryptedCredentials.access_token,
        refresh_token: decryptedCredentials.refresh_token,
        expires_at: data.expires_at,
        scope: data.scope,
        token_type: data.token_type
      };
    } catch (error) {
      console.error('Error retrieving credentials:', error);
      throw new Error('Failed to retrieve credentials');
    }
  }

  /**
   * Validate if credentials exist and are still valid
   */
  static async validateCredentials(userId: string): Promise<CredentialsValidationResult> {
    try {
      const credentials = await this.getCredentials(userId);
      
      if (!credentials) {
        return {
          isValid: false,
          needsRefresh: false,
          error: 'No credentials found'
        };
      }

      const expiresAt = new Date(credentials.expires_at);
      const now = new Date();
      const timeUntilExpiry = expiresAt.getTime() - now.getTime();
      const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds

      // Token has already expired
      if (timeUntilExpiry <= 0) {
        return {
          isValid: false,
          needsRefresh: true,
          error: 'Token has expired'
        };
      }

      // Token expires within 1 hour
      if (timeUntilExpiry <= oneHour) {
        return {
          isValid: true,
          needsRefresh: true
        };
      }

      // Token is valid
      return {
        isValid: true,
        needsRefresh: false
      };
    } catch (error) {
      console.error('Error validating credentials:', error);
      return {
        isValid: false,
        needsRefresh: false,
        error: 'Failed to validate credentials'
      };
    }
  }

  /**
   * Remove credentials for a user
   */
  static async removeCredentials(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('microsoft365_credentials')
        .delete()
        .eq('user_id', userId);

      if (error) {
        console.error('Error removing credentials:', error);
        throw new Error('Failed to remove credentials');
      }

      console.log('Successfully removed credentials for user:', userId);
    } catch (error) {
      console.error('Error in removeCredentials:', error);
      throw error;
    }
  }
}
